import type { CSSProperties, ReactNode } from "react";
import { createPortal } from "react-dom";
import styles from "./chartTooltip.module.css";

// Re-exported so chart callers build the tooltip body (head/dot/time/body/...)
// from this one module rather than cross-importing chartTooltip.module.css.
export const tooltipStyles = styles;

export type ChartTooltipAnchor = {
  readonly cx: number;
  readonly top: number;
  readonly bottom: number;
};

type ChartTooltipProps = {
  readonly anchor: ChartTooltipAnchor;
  readonly children: ReactNode;
};

// Fixed-position card portaled to <body> so it never clips inside the
// Instances table or a modal, and shows instantly (no native title delay).
// Content-agnostic: callers pass their own .tipHead / .tipBody / list markup.
export const ChartTooltip = ({ anchor, children }: ChartTooltipProps): JSX.Element => {
  const placeBelow = anchor.top < 160;
  const left = Math.min(Math.max(anchor.cx, 150), window.innerWidth - 150);
  const style: CSSProperties = placeBelow
    ? { left, top: anchor.bottom + 8, transform: "translateX(-50%)" }
    : { left, top: anchor.top - 8, transform: "translate(-50%, -100%)" };
  return createPortal(
    <div
      className={`${styles.tip} ${placeBelow ? styles.tipBelow : styles.tipAbove}`}
      style={style}
      role="tooltip"
    >
      {children}
    </div>,
    document.body,
  );
};
